import type { MouseEvent, PointerEvent, ReactNode } from 'react';
import { Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/utils';
import { ROUTES, PAGES_WITHOUT_NAV, NO_LAYOUT_PAGES } from '@/constants';
import { ProbabilityEventBackground } from '@/modules/probability-event/components/backgrounds/probability-event.background';
import { useAuthFlow } from '@/hooks';

interface LayoutMainProps {
	children: ReactNode;
	currentPageName: string;
	isLoading: boolean;
	userError?: boolean;
	loginPointerProps?: {
		onPointerDown: (e: PointerEvent) => void;
		onClick: (e: MouseEvent) => void;
	};
	isCrowdLockOpen: boolean;
	isRegisteredUser: boolean;
	isWebSidebarExpanded?: boolean;
	isProbabilityLayout?: boolean;
	isProbabilityEventEditProfilePage?: boolean;
	isProbabilityAchievementsPage?: boolean;
	isProbabilityFocusedPage?: boolean;
	isProbabilityPerformanceSummaryPage?: boolean;
	isProbabilityNewsfeedPage?: boolean;
	isProbabilityPinnedPage?: boolean;
}

export function LayoutMain({
	children,
	currentPageName,
	isLoading,
	userError,
	loginPointerProps,
	isCrowdLockOpen,
	isProbabilityLayout = false,
	isProbabilityEventEditProfilePage = false,
	isProbabilityAchievementsPage = false,
	isProbabilityFocusedPage = false,
	isProbabilityPerformanceSummaryPage = false,
	isProbabilityNewsfeedPage = false,
	isProbabilityPinnedPage = false,
	isRegisteredUser,
	isWebSidebarExpanded = false,
}: LayoutMainProps) {
	const { isWTN } = useAuthFlow();
	const showProbabilityBackground =
		isProbabilityLayout &&
		!isProbabilityEventEditProfilePage &&
		!isProbabilityAchievementsPage &&
		!isProbabilityPerformanceSummaryPage &&
		!isProbabilityNewsfeedPage;
	const isLobbyDetailsPage = currentPageName === 'LobbyDetails';
	const isHomePage = currentPageName === 'Home';
	// Newsfeed supplies its own mobile header; do not reserve space for the hidden shared one.
	const probabilityTopPadding = isProbabilityNewsfeedPage
		? 'pt-0 lg:pt-[72px]'
		: isWTN && isProbabilityPerformanceSummaryPage
			? 'pt-[96px] md:pt-[96px] lg:pt-[72px]'
			: 'pt-[54px] md:pt-[56px] lg:pt-[72px]';

	return (
		<main
			className={cn(
				'px-0 ml-0',
				isProbabilityEventEditProfilePage
					? 'relative isolate mt-0 min-h-dvh h-dvh overflow-hidden bg-transparent pt-0 pb-0 text-[#1C2D4A]'
					: isProbabilityLayout
						? isProbabilityAchievementsPage
							? 'relative isolate pt-0 lg:pt-[72px] mt-0 min-h-screen bg-[linear-gradient(123deg,#FFF_49.94%,#DCE3F9_100%)] pb-0 lg:pb-6 text-[#1C2D4A]'
							: cn(
									'relative isolate mt-0 text-[#1C2D4A]',
									isProbabilityPinnedPage
										? 'max-lg:h-dvh max-lg:min-h-dvh max-lg:overflow-hidden lg:min-h-screen'
										: 'min-h-screen',
									probabilityTopPadding,
									isProbabilityPerformanceSummaryPage || isProbabilityNewsfeedPage
										? 'bg-white'
										: 'bg-[#F7F9FC]',
									isProbabilityFocusedPage
										? 'pb-0'
										: isProbabilityPinnedPage
											? 'max-lg:pb-0 pb-[90px] lg:pb-6'
											: 'pb-[90px] lg:pb-6'
								)
						: cn(
								'lg:pb-0 lg:mt-0',
								PAGES_WITHOUT_NAV.includes(currentPageName) || isCrowdLockOpen
									? 'pb-0 mt-0'
									: !isRegisteredUser
										? 'pb-40 mt-[80px]'
										: 'pb-10 mt-[80px]'
							),
				!isProbabilityLayout &&
					!NO_LAYOUT_PAGES.includes(currentPageName) &&
					(isHomePage
						? isWebSidebarExpanded
							? 'lg:ml-[324px] overflow-x-hidden transition-[margin-left] duration-300 ease-out'
							: 'lg:ml-[120px] overflow-x-hidden transition-[margin-left] duration-300 ease-out'
						: isLobbyDetailsPage
							? 'lg:ml-64 overflow-x-hidden'
							: 'lg:ml-64 lg:mr-28 xl:mr-64 md:mr-20 overflow-x-hidden')
			)}
		>
			{showProbabilityBackground && <ProbabilityEventBackground />}
			{isLoading ? (
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-custom-spin mx-auto"></div>
						<p className="mt-4 text-gray-400">Loading...</p>
					</div>
				</div>
			) : userError && window.location.pathname !== ROUTES.HOME ? (
				<div className="flex items-center justify-center h-64">
					<div className="text-center max-w-md mx-auto p-6 bg-black/40 backdrop-blur-md rounded-xl border border-white/10">
						<Shield className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
						<h2 className="text-xl font-bold mb-2">Authentication Required</h2>
						<p className="text-gray-400 mb-6">You need to sign in to access this page</p>
						<Button {...loginPointerProps} className="bg-blue-600 hover:bg-blue-700">
							Sign In
						</Button>
					</div>
				</div>
			) : (
				children
			)}
		</main>
	);
}
